'use client'

import { Menu, Plus, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { usePathname } from 'next/navigation'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { BotaoAgencia } from '@/components/agencia/BotaoAgencia'
import { Icone } from '@/components/agencia/Icone'
import { LogoMarca } from '@/components/agencia/LogoMarca'
import type { MarcaResolvida } from '@/components/agencia/modeloMarca'
import { LocaleLink } from '@/components/LocaleLink'
import { buildLocalizedPath, stripPathForLocaleSwitch } from '@/modules/languages/lib/localizedPath'
import { cn } from '@/utilities/ui'

import type { NavItem, NavModel } from './modeloNav'

export type IdiomaOpcao = { code: string; label: string }

type Props = {
  nav: NavModel
  marca: MarcaResolvida
  idiomas: { enabled: boolean; current: string; default: string; lista: IdiomaOpcao[] }
}

const LANG_PREF_COOKIE = 'lang_pref'

function SeletorIdioma({ idiomas, tone }: { idiomas: Props['idiomas']; tone: 'claro' | 'escuro' }) {
  const pathname = usePathname() || '/'
  if (!idiomas.enabled || idiomas.lista.length < 2) return null
  const base = stripPathForLocaleSwitch(pathname)
  return (
    <div
      className={cn('flex items-center p-1', tone === 'escuro' ? 'bg-white/10' : 'bg-black/5')}
      aria-label="Idioma"
      role="group"
    >
      {idiomas.lista.map((l) => {
        const ativo = l.code === idiomas.current
        return (
          <a
            key={l.code}
            href={buildLocalizedPath(base, l.code, idiomas.default)}
            aria-current={ativo ? 'true' : undefined}
            aria-label={`Mudar para ${l.label}`}
            onClick={() => {
              document.cookie = `${LANG_PREF_COOKIE}=${encodeURIComponent(l.code)};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`
            }}
            className={cn(
              'ag-mono flex h-full w-[49px] items-center justify-center px-3 py-2 text-[12px] transition-all duration-200',
              ativo
                ? tone === 'escuro'
                  ? 'bg-white text-black'
                  : 'bg-black text-white'
                : tone === 'escuro'
                  ? 'text-white hover:text-white/80'
                  : 'text-black hover:text-black/70',
            )}
          >
            {l.code.toUpperCase()}
          </a>
        )
      })}
    </div>
  )
}

function MegaMenu({ item, onFechar }: { item: NavItem; onFechar: () => void }) {
  const [ativo, setAtivo] = useState(0)
  const sub = item.submenu[ativo] ?? item.submenu[0]
  return (
    <motion.div
      key={item.rotulo}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="border-t border-black/10 bg-white text-black"
      role="region"
      aria-label={item.rotulo}
    >
      <div className="mx-auto grid w-full max-w-[1500px] grid-cols-[1fr_1fr_280px] gap-10 px-16 py-10">
        <ul className="flex flex-col gap-1">
          {item.submenu.map((s, i) => (
            <li key={i}>
              <LocaleLink
                href={s.href}
                onMouseEnter={() => setAtivo(i)}
                onFocus={() => setAtivo(i)}
                onClick={onFechar}
                className={cn(
                  'ag-heading flex items-center gap-3 py-2 text-[24px] leading-tight transition-opacity duration-200',
                  ativo === i ? 'opacity-100' : 'opacity-70 hover:opacity-100',
                )}
              >
                {s.icone ? <Icone nome={s.icone} size={18} className="text-[var(--ag-primary)]" /> : null}
                {s.rotulo}
              </LocaleLink>
            </li>
          ))}
          {item.verTodos ? (
            <li className="pt-6">
              <LocaleLink
                href={item.verTodos.href}
                onClick={onFechar}
                className="ag-mono inline-flex h-[42px] items-center gap-3 border border-black px-5 text-[12px] transition-colors hover:bg-black hover:text-white"
              >
                {item.verTodos.rotulo}
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                  <circle cx="9" cy="7" r="1.9" fill="currentColor" />
                  <circle cx="9" cy="17" r="1.9" fill="currentColor" />
                  <circle cx="15" cy="12" r="1.9" fill="currentColor" />
                </svg>
              </LocaleLink>
            </li>
          ) : null}
        </ul>
        <div className="pt-2">
          <AnimatePresence mode="wait">
            <motion.p
              key={ativo}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="max-w-[360px] text-[14px] leading-[1.5] text-black/70"
            >
              {sub?.descricao ?? ''}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="relative h-[210px] w-full overflow-hidden bg-[var(--ag-surface-light)]">
          <AnimatePresence mode="wait">
            {sub?.imagemUrl ? (
              <motion.img
                key={sub.imagemUrl}
                src={sub.imagemUrl}
                alt={sub.imagemAlt ?? ''}
                className="absolute inset-0 h-full w-full object-cover"
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              />
            ) : (
              <motion.div
                key="ph"
                className="absolute inset-0"
                style={{ background: 'linear-gradient(135deg, rgb(var(--ag-primary-rgb) / 0.9), rgb(var(--ag-primary-rgb) / 0.4))' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}

export function HeaderAgenciaClient({ nav, marca, idiomas }: Props) {
  const reduce = useReducedMotion()
  const [compacto, setCompacto] = useState(false)
  const [aberto, setAberto] = useState<number | null>(null)
  const [movel, setMovel] = useState(false)
  const [acordeao, setAcordeao] = useState<number | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pathname = usePathname()

  const [desktop, setDesktop] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const onMq = () => setDesktop(mq.matches)
    onMq()
    mq.addEventListener('change', onMq)
    return () => mq.removeEventListener('change', onMq)
  }, [])

  useEffect(() => {
    if (!nav.encolherAoRolar) return
    const onScroll = () => setCompacto((window.scrollY || document.documentElement.scrollTop) > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [nav.encolherAoRolar])

  // Fecha tudo ao navegar.
  useEffect(() => {
    setAberto(null)
    setMovel(false)
  }, [pathname])

  useEffect(() => {
    document.documentElement.style.overflow = movel ? 'hidden' : ''
    return () => {
      document.documentElement.style.overflow = ''
    }
  }, [movel])

  const abrir = useCallback((i: number) => {
    if (timer.current) clearTimeout(timer.current)
    setAberto(i)
  }, [])
  const fecharComAtraso = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setAberto(null), 120)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAberto(null)
        setMovel(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const megaAberto = aberto != null && (nav.itens[aberto]?.submenu.length ?? 0) > 0
  const tone: 'claro' | 'escuro' = megaAberto || movel ? 'claro' : 'escuro'
  const itemAberto = megaAberto ? nav.itens[aberto!] : null
  /** Pílula centrada: só no desktop, depois de rolar e sem mega menu aberto. */
  const pilula = desktop && compacto && !megaAberto

  return (
    <>
      <AnimatePresence>
        {megaAberto || movel ? (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-md"
            aria-hidden
            onClick={() => {
              setAberto(null)
              setMovel(false)
            }}
          />
        ) : null}
      </AnimatePresence>

      <div className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center">
      <motion.nav
        data-header="agencia"
        data-tone={tone}
        data-compacto={pilula ? 'true' : 'false'}
        className="pointer-events-auto w-full max-w-full"
        initial={false}
        animate={{ width: pilula ? '80%' : '100%', marginTop: pilula ? 16 : 0 }}
        transition={reduce ? { duration: 0 } : { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        onMouseLeave={fecharComAtraso}
      >
        <div
          className={cn(
            'w-full overflow-hidden transition-[background-color,border-color,padding] duration-300',
            tone === 'claro'
              ? 'border-b border-black/10 bg-white text-black'
              : pilula
                ? 'border border-white/15 bg-[rgb(var(--ag-bg-dark-rgb)/0.6)] text-white backdrop-blur-lg'
                : 'border-b border-white/10 bg-[var(--ag-bg-dark)] text-white',
            pilula ? 'px-4' : 'px-4 lg:px-16',
          )}
        >
          <div className="relative flex items-center justify-between gap-6 py-4 lg:py-[18px]">
            <LogoMarca marca={marca} tone={tone} compacto={pilula} alturaMovel={28} className="min-w-0 shrink" />

            <div className="hidden flex-1 items-center justify-center gap-10 lg:flex">
              {nav.itens.map((item, i) => {
                const temSub = item.submenu.length > 0
                const Tag = (item.href ? LocaleLink : 'button') as React.ElementType
                const props = item.href ? { href: item.href } : { type: 'button' as const }
                return (
                  <div
                    key={i}
                    className="flex items-center"
                    onMouseEnter={() => (temSub ? abrir(i) : setAberto(null))}
                    onFocus={() => (temSub ? abrir(i) : setAberto(null))}
                  >
                    <Tag
                      {...(props as object)}
                      className={cn(
                        'ag-mono flex items-center gap-[10px] text-[14px] transition-colors duration-200',
                        tone === 'claro' ? 'text-black hover:text-black/70' : 'text-white hover:text-white/80',
                      )}
                      aria-haspopup={temSub ? 'true' : undefined}
                      aria-expanded={temSub ? aberto === i : undefined}
                      onClick={temSub && !item.href ? () => setAberto(aberto === i ? null : i) : undefined}
                    >
                      {item.rotulo}
                      {temSub ? (
                        <Plus className={cn('h-4 w-4 transition-transform duration-300', aberto === i && 'rotate-45')} strokeWidth={1.5} />
                      ) : null}
                    </Tag>
                  </div>
                )
              })}
            </div>

            <div className="hidden items-stretch gap-[10px] lg:flex">
              {nav.mostrarIdiomas ? <SeletorIdioma idiomas={idiomas} tone={tone} /> : null}
              {nav.cta ? <BotaoAgencia botao={nav.cta} tamanho="normal" /> : null}
            </div>

            <div className="flex items-center lg:hidden">
              <button
                type="button"
                onClick={() => setMovel((v) => !v)}
                aria-label={movel ? 'Fechar menu' : nav.rotuloMenuMovel}
                aria-expanded={movel}
                className={cn(
                  'flex h-10 w-10 items-center justify-center transition-colors duration-150',
                  tone === 'claro' ? 'bg-black/5 text-black' : 'bg-white/10 text-white',
                )}
              >
                {movel ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {itemAberto ? (
            <div className="hidden lg:block" onMouseEnter={() => abrir(aberto!)}>
              <MegaMenu item={itemAberto} onFechar={() => setAberto(null)} />
            </div>
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {movel ? (
            <motion.div
              key="movel"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="max-h-[calc(100dvh-80px)] overflow-y-auto bg-white px-4 pb-6 pt-2 text-black lg:hidden"
            >
              <ul className="flex flex-col">
                {nav.itens.map((item, i) => {
                  const temSub = item.submenu.length > 0
                  const ab = acordeao === i
                  return (
                    <li key={i} className="border-b border-black/10">
                      <div className="flex items-center justify-between">
                        {item.href ? (
                          <LocaleLink href={item.href} className="ag-mono flex-1 py-4 text-[15px]" onClick={() => setMovel(false)}>
                            {item.rotulo}
                          </LocaleLink>
                        ) : (
                          <button type="button" className="ag-mono flex-1 py-4 text-left text-[15px]" onClick={() => setAcordeao(ab ? null : i)}>
                            {item.rotulo}
                          </button>
                        )}
                        {temSub ? (
                          <button
                            type="button"
                            aria-label={ab ? `Fechar ${item.rotulo}` : `Abrir ${item.rotulo}`}
                            aria-expanded={ab}
                            onClick={() => setAcordeao(ab ? null : i)}
                            className="flex h-10 w-10 items-center justify-center"
                          >
                            <Plus className={cn('h-5 w-5 transition-transform duration-300', ab && 'rotate-45')} strokeWidth={1.5} />
                          </button>
                        ) : null}
                      </div>
                      <AnimatePresence initial={false}>
                        {temSub && ab ? (
                          <motion.ul
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                          >
                            {item.submenu.map((s, si) => (
                              <li key={si}>
                                <LocaleLink href={s.href} className="flex items-center gap-3 py-3 pl-2 text-[15px]" onClick={() => setMovel(false)}>
                                  {s.icone ? <Icone nome={s.icone} size={16} className="text-[var(--ag-primary)]" /> : null}
                                  {s.rotulo}
                                </LocaleLink>
                              </li>
                            ))}
                            {item.verTodos ? (
                              <li className="pb-3 pt-1">
                                <LocaleLink href={item.verTodos.href} className="ag-mono text-[12px] underline underline-offset-4" onClick={() => setMovel(false)}>
                                  {item.verTodos.rotulo}
                                </LocaleLink>
                              </li>
                            ) : null}
                          </motion.ul>
                        ) : null}
                      </AnimatePresence>
                    </li>
                  )
                })}
              </ul>
              <div className="mt-6 flex items-stretch gap-3">
                {nav.mostrarIdiomas ? <SeletorIdioma idiomas={idiomas} tone="claro" /> : null}
                {nav.cta ? <BotaoAgencia botao={nav.cta} className="flex-1 justify-between" /> : null}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.nav>
      </div>
    </>
  )
}
